import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	computed,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { EMPTY, Subscription, catchError, finalize, switchMap, takeWhile, timer } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadFileService } from '@/common/download-file.service';
import { BoxListApiService } from '@/tms/box-list/_api/box-list.api.service';
import { FormatDateTimePipe } from '@/common/pipes/format-date-time.pipe';
import { GboxScansApiService } from '@/tms/gbox-scans/_api/gbox-scans.api.service';
import { OcrStatus, Scan, ScanDialogResult } from '@/tms/gbox-scans/_models/gbox-scans.model';

interface OcrStatusMeta {
	label: string;
	icon: string;
	cssClass: string;
}

export interface ScanPreviewDialogData {
	boxId: number;
	scanId: number;
	name: string;
	scan?: Scan;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.25;
const WHEEL_ZOOM_STEP = 1.1;
const OCR_POLL_INTERVAL_MS = 2500;
const OCR_POLL_TIMEOUT_MS = 130000;
const OCR_PENDING_STATUSES: ReadonlyArray<OcrStatus> = ['pending', 'processing'];

@Component({
	selector: 'app-scan-preview-dialog',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		RouterLink,
		FormatDateTimePipe,
	],
	templateUrl: './scan-preview-dialog.component.html',
	styleUrl: './scan-preview-dialog.component.scss',
})
export class ScanPreviewDialogComponent implements OnInit, OnDestroy {
	readonly data = inject<ScanPreviewDialogData>(MAT_DIALOG_DATA);

	readonly ocrStatusMeta: Readonly<Record<OcrStatus, OcrStatusMeta>> = {
		pending: { label: 'Oczekuje na OCR', icon: 'fa-hourglass-half', cssClass: 'ocr-status--pending' },
		processing: { label: 'OCR w toku', icon: 'fa-spinner fa-spin', cssClass: 'ocr-status--processing' },
		completed: { label: 'OCR zakończony', icon: 'fa-circle-check', cssClass: 'ocr-status--completed' },
		failed: { label: 'OCR nie powiódł się', icon: 'fa-circle-exclamation', cssClass: 'ocr-status--failed' },
		skipped: { label: 'OCR pominięty', icon: 'fa-circle-minus', cssClass: 'ocr-status--skipped' },
	};

	readonly loading = signal(true);
	readonly error = signal(false);
	readonly isImage = signal(false);
	readonly isPdf = signal(false);
	readonly imageSrc = signal<string | null>(null);
	readonly pdfSrc = signal<SafeResourceUrl | null>(null);

	readonly zoom = signal(1);
	readonly rotation = signal(0);
	readonly panX = signal(0);
	readonly panY = signal(0);
	readonly viewportSize = signal({ w: 0, h: 0 });
	readonly imageNaturalSize = signal({ w: 0, h: 0 });
	readonly fitScale = computed(() => {
		if (this.rotation() % 180 === 0) {
			return 1;
		}
		const { w: vw, h: vh } = this.viewportSize();
		const { w: iw, h: ih } = this.imageNaturalSize();
		if (!vw || !vh || !iw || !ih) {
			return 1;
		}
		const base = Math.min(vw / iw, vh / ih, 1);
		if (!base) {
			return 1;
		}
		const extra = Math.min(vw / (ih * base), vh / (iw * base));
		return Math.min(extra, 1 / base);
	});
	readonly transform = computed(
		() =>
			`translate(${this.panX()}px, ${this.panY()}px) rotate(${this.rotation()}deg) scale(${this.zoom() * this.fitScale()})`,
	);
	readonly zoomPercent = computed(() => `${Math.round(this.zoom() * 100)}%`);

	readonly scan = signal<Scan | null>(null);
	readonly recomputingOcr = signal(false);
	readonly recomputeOcrError = signal<string | null>(null);

	readonly summaryText = computed(() => (this.scan()?.ocrSummary ?? '').trim());

	@ViewChild('viewport') private readonly viewportRef?: ElementRef<HTMLElement>;

	private objectUrl: string | null = null;
	private blob: Blob | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private ocrPollSub: Subscription | null = null;
	private dragging = false;
	private dragStartX = 0;
	private dragStartY = 0;
	private dragOriginX = 0;
	private dragOriginY = 0;

	private readonly api = inject(BoxListApiService);
	private readonly scanApi = inject(GboxScansApiService);
	private readonly sanitizer = inject(DomSanitizer);
	private readonly downloader = inject(DownloadFileService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialogRef =
		inject<MatDialogRef<ScanPreviewDialogComponent, ScanDialogResult | undefined>>(MatDialogRef);

	ngOnInit(): void {
		if (this.data.scan) {
			this.scan.set(this.data.scan);
			if (OCR_PENDING_STATUSES.includes(this.data.scan.ocrStatus)) {
				this.startOcrPolling(this.data.scan.id);
			}
		}

		this.api
			.getScanContent(this.data.boxId, this.data.scanId)
			.pipe(
				catchError(() => {
					this.error.set(true);
					return EMPTY;
				}),
				finalize(() => this.loading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((blob) => this.handleBlob(blob));
	}

	download(): void {
		if (this.blob) {
			this.downloader.downloadBlob(this.blob, this.data.name);
		}
	}

	close(): void {
		const current = this.scan();
		this.dialogRef.close(current ? { updated: current, openNext: false } : undefined);
	}

	zoomIn(): void {
		this.setZoom(this.zoom() * ZOOM_STEP);
	}

	zoomOut(): void {
		this.setZoom(this.zoom() / ZOOM_STEP);
	}

	rotateLeft(): void {
		this.rotation.update((deg) => this.normalizeRotation(deg - 90));
	}

	rotateRight(): void {
		this.rotation.update((deg) => this.normalizeRotation(deg + 90));
	}

	resetView(): void {
		this.zoom.set(1);
		this.rotation.set(0);
		this.panX.set(0);
		this.panY.set(0);
	}

	onImageLoad(event: Event): void {
		const img = event.target as HTMLImageElement;
		this.imageNaturalSize.set({ w: img.naturalWidth, h: img.naturalHeight });
		this.observeViewport();
	}

	onWheel(event: WheelEvent): void {
		event.preventDefault();
		this.setZoom(this.zoom() * (event.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP));
	}

	onPointerDown(event: PointerEvent): void {
		if (this.zoom() <= 1) {
			return;
		}
		this.dragging = true;
		this.dragStartX = event.clientX;
		this.dragStartY = event.clientY;
		this.dragOriginX = this.panX();
		this.dragOriginY = this.panY();
		(event.target as Element | null)?.setPointerCapture?.(event.pointerId);
	}

	onPointerMove(event: PointerEvent): void {
		if (!this.dragging) {
			return;
		}
		this.panX.set(this.dragOriginX + (event.clientX - this.dragStartX));
		this.panY.set(this.dragOriginY + (event.clientY - this.dragStartY));
	}

	onPointerUp(): void {
		this.dragging = false;
	}

	recomputeOcr(): void {
		const current = this.scan();
		if (!current || this.recomputingOcr() || current.archiveStatus !== 'archived') {
			return;
		}
		this.recomputeOcrError.set(null);
		this.recomputingOcr.set(true);

		this.scanApi
			.recomputeOcr(current.id)
			.pipe(
				catchError((err: unknown) => {
					const message =
						err instanceof HttpErrorResponse && typeof err.error?.message === 'string'
							? err.error.message
							: 'Nie udało się uruchomić ponownego przeliczenia OCR.';
					this.recomputeOcrError.set(message);
					return EMPTY;
				}),
				finalize(() => this.recomputingOcr.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => {
				this.applyOcrUpdate(res.data);
				this.startOcrPolling(res.data.id);
			});
	}

	ngOnDestroy(): void {
		this.stopOcrPolling();
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
		if (this.objectUrl) {
			URL.revokeObjectURL(this.objectUrl);
		}
	}

	private observeViewport(): void {
		const el = this.viewportRef?.nativeElement;
		if (!el) {
			return;
		}
		this.updateViewportSize(el);
		if (this.resizeObserver) {
			return;
		}
		this.resizeObserver = new ResizeObserver(() => this.updateViewportSize(el));
		this.resizeObserver.observe(el);
	}

	private updateViewportSize(el: HTMLElement): void {
		this.viewportSize.set({ w: el.clientWidth, h: el.clientHeight });
	}

	private startOcrPolling(id: number): void {
		this.stopOcrPolling();
		const deadline = Date.now() + OCR_POLL_TIMEOUT_MS;
		this.ocrPollSub = timer(OCR_POLL_INTERVAL_MS, OCR_POLL_INTERVAL_MS)
			.pipe(
				takeWhile(() => Date.now() < deadline),
				switchMap(() => this.scanApi.get(id)),
				takeWhile((res) => OCR_PENDING_STATUSES.includes(res.data.ocrStatus), true),
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => this.applyOcrUpdate(res.data));
	}

	private applyOcrUpdate(scan: Scan): void {
		this.scan.set(scan);
	}

	private stopOcrPolling(): void {
		this.ocrPollSub?.unsubscribe();
		this.ocrPollSub = null;
	}

	private setZoom(value: number): void {
		const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
		this.zoom.set(clamped);
		if (clamped <= 1) {
			this.panX.set(0);
			this.panY.set(0);
		}
	}

	private normalizeRotation(deg: number): number {
		return ((deg % 360) + 360) % 360;
	}

	private handleBlob(blob: Blob): void {
		this.blob = blob;
		this.objectUrl = URL.createObjectURL(blob);
		this.isImage.set(blob.type.startsWith('image/'));
		this.isPdf.set(blob.type === 'application/pdf');

		if (this.isImage()) {
			this.imageSrc.set(this.objectUrl);
		} else if (this.isPdf()) {
			this.pdfSrc.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));
		}
	}
}
